import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { readingsAPI, type CreateReadingRequest, type UpdateReadingRequest } from '../api/readings';
import { toast } from '../components/common';

// Reading Queries
export const useReading = (id: string) => {
  return useQuery({
    queryKey: ['readings', id],
    queryFn: () => readingsAPI.getReading(id),
    enabled: !!id,
  });
};

export const useReadings = (
  params?: { page?: number; limit?: number; favorite?: boolean },
  options?: { enabled?: boolean }
) => {
  console.log('[useReadings Hook] Called with params:', params); // Debug log
  
  return useQuery({
    queryKey: ['readings', 'list', params],
    queryFn: () => {
      console.log('[useReadings Hook] queryFn executing with params:', params); // Debug log
      return readingsAPI.getReadings(params);
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: options?.enabled !== false,
  });
};

export const useReadingStats = () => {
  return useQuery({
    queryKey: ['readings', 'stats'],
    queryFn: () => readingsAPI.getStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Reading Mutations
export const useCreateReading = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateReadingRequest) => readingsAPI.createReading(data),
    onSuccess: (response) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['readings'] });
      
      toast.success('Reading saved successfully!');
      return response.data;
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to save reading';
      toast.error(message);
    },
  });
};

export const useUpdateReading = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReadingRequest }) => 
      readingsAPI.updateReading(id, data),
    onSuccess: (response, variables) => {
      // Update the specific reading in cache
      queryClient.setQueryData(['readings', variables.id], response);
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['readings', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['readings', 'stats'] });
      
      toast.success('Reading updated successfully!');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update reading';
      toast.error(message);
    },
  });
};

export const useDeleteReading = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => readingsAPI.deleteReading(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['readings', id] });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['readings', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['readings', 'stats'] });
      
      toast.success('Reading deleted successfully!');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to delete reading';
      toast.error(message);
    },
  });
};

export const useCreateDeepDive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { parentReadingId: string; question: string; cardCount: number }) =>
      readingsAPI.createDeepDive(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['readings'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to start deep dive';
      toast.error(message);
    },
  });
};

export const useToggleFavorite = () => {
  const updateReading = useUpdateReading();
  
  return useMutation({
    mutationFn: ({ id, isFavorite }: { id: string; isFavorite: boolean }) => 
      updateReading.mutateAsync({ id, data: { isFavorite } }),
    onSuccess: () => {
      // Success handled by updateReading
    },
  });
};